import React, { useState, useEffect } from 'react';

interface AdaptivePathwayPreviewProps {
  onClick?: () => void;
}

const AdaptivePathwayPreview: React.FC<AdaptivePathwayPreviewProps> = ({ onClick }) => {
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [pulseNode, setPulseNode] = useState<number | null>(null);

  // Cycle through highlighting different nodes
  useEffect(() => {
    const interval = setInterval(() => {
      setHighlightIndex((prev) => (prev + 1) % 7);
    }, 800);
    return () => clearInterval(interval);
  }, []);

  // Pulse effect on random nodes
  useEffect(() => {
    const interval = setInterval(() => {
      setPulseNode(Math.floor(Math.random() * 12));
      setTimeout(() => setPulseNode(null), 400);
    }, 1500);
    return () => clearInterval(interval);
  }, []);

  // Hexagon layout - interlocking pattern
  const hexWidth = 32;
  const hexHeight = 28;
  const horizontalSpacing = hexWidth * 0.85;
  const verticalSpacing = hexHeight * 0.75;

  // Generate interlocking hexagon positions
  const hexagons = [
    // Row 0
    { row: 0, col: 2, state: 'complete' },
    { row: 0, col: 3, state: 'complete' },
    // Row 1 (offset)
    { row: 1, col: 1, state: 'complete' },
    { row: 1, col: 2, state: 'complete' },
    { row: 1, col: 3, state: 'available' },
    // Row 2
    { row: 2, col: 1, state: 'complete' },
    { row: 2, col: 2, state: 'available' },
    { row: 2, col: 3, state: 'pending' },
    // Row 3 (offset)
    { row: 3, col: 1, state: 'pending' },
    { row: 3, col: 2, state: 'pending' },
    { row: 3, col: 3, state: 'pending' },
    // Row 4
    { row: 4, col: 2, state: 'locked' },
  ];

  const getHexPosition = (row: number, col: number) => {
    const isOffset = row % 2 === 1;
    const xOffset = isOffset ? horizontalSpacing / 2 : 0;
    return {
      x: 30 + col * horizontalSpacing + xOffset,
      y: 20 + row * verticalSpacing,
    };
  };

  const getStateColor = (state: string, index: number) => {
    const isHighlighted = highlightIndex === index;
    const isPulsing = pulseNode === index;

    if (state === 'complete') return isHighlighted ? '#10b981' : '#10b981';
    if (state === 'available') return isHighlighted ? '#0ea5e9' : '#3b82f6';
    if (state === 'pending') return isPulsing ? '#f59e0b' : '#64748b';
    return '#334155';
  };

  const getStateOpacity = (state: string, index: number) => {
    if (highlightIndex === index) return 1;
    if (state === 'complete') return 0.9;
    if (state === 'available') return 0.85;
    if (state === 'pending') return 0.5;
    return 0.25;
  };

  // Mini hexagon path
  const hexPath = (cx: number, cy: number, size: number) => {
    const points = [];
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i - Math.PI / 2;
      points.push(`${cx + size * Math.cos(angle)},${cy + size * Math.sin(angle)}`);
    }
    return `M ${points.join(' L ')} Z`;
  };

  return (
    <div
      className="relative cursor-pointer transition-all duration-300 hover:scale-[1.02] group"
      onClick={onClick}
      style={{
        background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        overflow: 'hidden',
        width: '320px',
      }}
    >
      {/* Header */}
      <div
        className="px-4 py-2 border-b flex items-center justify-between"
        style={{
          borderColor: 'rgba(255, 255, 255, 0.1)',
          background: 'rgba(245, 158, 11, 0.05)',
        }}
      >
        <span className="text-xs font-semibold" style={{ color: '#94a3b8' }}>
          Adaptive Learning Path
        </span>
        <div className="flex items-center gap-1">
          <svg className="w-3 h-3" style={{ color: '#f59e0b' }} viewBox="0 0 24 24" fill="currentColor">
            <path d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <span className="text-[10px]" style={{ color: '#f59e0b' }}>
            AI-Powered
          </span>
        </div>
      </div>

      {/* Hexagon Grid */}
      <div className="p-4">
        <svg width="280" height="180" className="block mx-auto">
          <defs>
            <filter id="hex-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="2" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <filter id="hex-pulse" x="-100%" y="-100%" width="300%" height="300%">
              <feGaussianBlur stdDeviation="4" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Connection lines between adjacent hexagons */}
          {hexagons.map((hex, i) => {
            const pos = getHexPosition(hex.row, hex.col);
            // Find adjacent hexagons to connect
            return hexagons.slice(i + 1).map((otherHex, j) => {
              const otherPos = getHexPosition(otherHex.row, otherHex.col);
              const distance = Math.sqrt(
                Math.pow(pos.x - otherPos.x, 2) + Math.pow(pos.y - otherPos.y, 2)
              );
              // Only connect close hexagons
              if (distance < horizontalSpacing * 1.3) {
                return (
                  <line
                    key={`line-${i}-${j}`}
                    x1={pos.x}
                    y1={pos.y}
                    x2={otherPos.x}
                    y2={otherPos.y}
                    stroke="#334155"
                    strokeWidth="1.5"
                    opacity="0.4"
                  />
                );
              }
              return null;
            });
          })}

          {/* Hexagons */}
          {hexagons.map((hex, index) => {
            const pos = getHexPosition(hex.row, hex.col);
            const color = getStateColor(hex.state, index);
            const opacity = getStateOpacity(hex.state, index);
            const isHighlighted = highlightIndex === index;
            const isPulsing = pulseNode === index;

            return (
              <g key={index}>
                {/* Shadow */}
                <path
                  d={hexPath(pos.x, pos.y + 3, 12)}
                  fill={color}
                  opacity={0.2}
                />
                {/* Main hexagon */}
                <path
                  d={hexPath(pos.x, pos.y, 12)}
                  fill={color}
                  opacity={opacity}
                  filter={isHighlighted || isPulsing ? 'url(#hex-pulse)' : 'url(#hex-glow)'}
                  style={{
                    transition: 'all 0.3s ease',
                    transform: isHighlighted ? 'scale(1.1)' : 'scale(1)',
                    transformOrigin: `${pos.x}px ${pos.y}px`,
                  }}
                />
                {/* Icon for complete/available */}
                {hex.state === 'complete' && (
                  <text
                    x={pos.x}
                    y={pos.y + 3}
                    textAnchor="middle"
                    fill="white"
                    fontSize="8"
                    fontWeight="bold"
                  >
                    ✓
                  </text>
                )}
                {hex.state === 'available' && (
                  <circle cx={pos.x} cy={pos.y} r="3" fill="white" opacity="0.9" />
                )}
              </g>
            );
          })}

          {/* Animated progress indicator */}
          <g>
            <circle
              cx="240"
              cy="30"
              r="18"
              fill="none"
              stroke="rgba(255,255,255,0.1)"
              strokeWidth="3"
            />
            <circle
              cx="240"
              cy="30"
              r="18"
              fill="none"
              stroke="#10b981"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray="70 113"
              style={{ transform: 'rotate(-90deg)', transformOrigin: '240px 30px' }}
            />
            <text x="240" y="34" textAnchor="middle" fill="#10b981" fontSize="10" fontWeight="bold">
              62%
            </text>
          </g>

          {/* Legend */}
          <g transform="translate(200, 140)">
            <circle cx="0" cy="0" r="4" fill="#10b981" />
            <text x="8" y="3" fill="#94a3b8" fontSize="8">Done</text>
            <circle cx="0" cy="14" r="4" fill="#3b82f6" />
            <text x="8" y="17" fill="#94a3b8" fontSize="8">Next</text>
            <circle cx="0" cy="28" r="4" fill="#64748b" opacity="0.5" />
            <text x="8" y="31" fill="#94a3b8" fontSize="8">Upcoming</text>
          </g>
        </svg>
      </div>

      {/* Status bar */}
      <div
        className="px-4 py-2 flex items-center justify-between"
        style={{
          background: 'rgba(255, 255, 255, 0.02)',
          borderTop: '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ background: '#10b981' }} />
          <span className="text-[10px]" style={{ color: '#94a3b8' }}>
            5 of 12 complete
          </span>
        </div>
        <span className="text-[10px]" style={{ color: '#f59e0b' }}>
          Path adapting...
        </span>
      </div>

      {/* Expand overlay */}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.9) 0%, rgba(249, 115, 22, 0.9) 100%)',
            boxShadow: '0 8px 32px rgba(245, 158, 11, 0.4)',
          }}
        >
          <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default AdaptivePathwayPreview;
